import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18next from 'i18next'
import { settingsService, subscriptionService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { queryKeys } from '@/config/queryKeys'
import type { UpdateProfileInput, UpdateFacilityInput } from '@/types'

export function useProfile() {
  return useQuery({
    queryKey: [...queryKeys.auth.user(), 'profile'],
    queryFn: () => settingsService.getProfile(),
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateProfileInput) => settingsService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all })
      toast.success(i18next.t('settings:profileSettings.profileUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('settings:profileSettings.updateFailed'))
    },
  })
}

export function useFacility() {
  return useQuery({
    queryKey: queryKeys.facility.current(),
    queryFn: () => settingsService.getFacility(),
  })
}

export function useUpdateFacility() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateFacilityInput) => settingsService.updateFacility(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facility.all })
      toast.success(i18next.t('settings:facilitySettings.facilityUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('settings:facilitySettings.updateFailed'))
    },
  })
}

export function useSubscriptionStatus() {
  const facility = useAuthStore((s) => s.facility)

  return useQuery({
    queryKey: [...queryKeys.facility.all, 'subscription'],
    queryFn: () => subscriptionService.getStatus(facility!.id),
    enabled: !!facility,
  })
}
